import React, { useState, useEffect } from "react";
import { Dialog, Box, Typography, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ReservableAsset } from "../../types/reservation";

import { useTheme as useMuiTheme } from "@mui/material";
import { t } from "i18next";

type ReservationMode = "single" | "continuous" | "recurring";
type RecurrencePattern = "daily" | "weekly" | "custom";

interface ReservationDialogProps {
  open: boolean;
  onClose: () => void;
  onReserve: (data: {
    startDate: string;
    endDate?: string;
    startTime?: string;
    endTime?: string;
    purpose?: string;
    isAllDay?: boolean;
    reservationType?: string;
    recurrencePattern?: string;
    customDays?: boolean[];
  }) => Promise<void>;
  asset: ReservableAsset | null;
}

const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const ReservationDialog: React.FC<ReservationDialogProps> = ({
  open,
  onClose,
  onReserve,
  asset,
}) => {
  const [mode, setMode] = useState<ReservationMode>("single");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [recurrencePattern, setRecurrencePattern] =
    useState<RecurrencePattern>("daily");
  const [customDays, setCustomDays] = useState<boolean[]>([
    false,
    true,
    true,
    true,
    true,
    true,
    false,
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const muiTheme = useMuiTheme();
  const isDarkMode = muiTheme.palette.mode === "dark";
  const themeColors = {
    background: isDarkMode ? "#121212" : "#f5f8ff",
    cardBg: isDarkMode ? "#1e1e1e" : "#ffffff",
    border: isDarkMode ? "rgba(255,255,255,0.12)" : "#e2e8f0",
    borderLight: isDarkMode ? "rgba(255,255,255,0.06)" : "#f1f5f9",
    textPrimary: isDarkMode ? "#e0e0e0" : "#1e293b",
    textSecondary: isDarkMode ? "rgba(224,224,224,0.7)" : "#64748b",
    textMuted: isDarkMode ? "rgba(224,224,224,0.5)" : "#94a3b8",
    inputBg: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
    tagBg: isDarkMode ? "rgba(255,255,255,0.08)" : "#f1f5f9",
    tagBgCategory: isDarkMode ? "rgba(217,119,6,0.15)" : "#fffbeb",
    tagTextCategory: isDarkMode ? "#fbbf24" : "#d97706",
    myBookingBg: isDarkMode ? "rgba(129,140,248,0.15)" : "#eef2ff",
    myBookingBorder: isDarkMode ? "rgba(129,140,248,0.3)" : "#e0e7ff",
    myBookingText: isDarkMode ? "#a5b4fc" : "#4338ca",
    otherBookingBg: isDarkMode ? "rgba(255,255,255,0.05)" : "#f8fafc",
    hoverBg: isDarkMode ? "rgba(255,255,255,0.08)" : "#f1f5f9",
    hoverBgDanger: isDarkMode ? "rgba(239,68,68,0.15)" : "#fef2f2",
    primary: isDarkMode ? "#6db1e7" : "#6db1e7",
    primaryHover: isDarkMode ? "#6366f1" : "#4338ca",
    emptyStateBg: isDarkMode ? "rgba(255,255,255,0.05)" : "#f1f5f9",
    emptyStateBorder: isDarkMode ? "rgba(255,255,255,0.12)" : "#cbd5e1",
    legendBg: isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(248,250,252,0.5)",
    addButtonBg: isDarkMode ? "#374151" : "#0f172a",
    addButtonHover: isDarkMode ? "#4b5563" : "#1e293b",
  };

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setMode("single");
      setStartDate(new Date().toISOString().split("T")[0]);
      setEndDate(new Date().toISOString().split("T")[0]);
      setStartTime("09:00");
      setEndTime("17:00");
      setRecurrencePattern("daily");
      setCustomDays([false, true, true, true, true, true, false]);
      setError(null);
    }
  }, [open]);

  // Sync end date with start date in single mode
  useEffect(() => {
    if (mode === "single") {
      setEndDate(startDate);
    }
  }, [mode, startDate]);

  useEffect(() => {
    if (
      !startDate ||
      !startTime ||
      !endTime ||
      (mode !== "single" && !endDate)
    ) {
      setIsFormValid(false);
      return;
    }

    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);

    if (endDateTime <= startDateTime) {
      setIsFormValid(false);
    } else {
      setIsFormValid(true);
    }
  }, [startDate, endDate, startTime, endTime, mode]);

  const toggleDay = (idx: number) => {
    const newDays = [...customDays];
    newDays[idx] = !newDays[idx];
    setCustomDays(newDays);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const { utcDate: utcStartDate, utcTime: utcStartTime } = {
        utcDate: startDate,
        utcTime: startTime,
      };
      const { utcDate: utcEndDate, utcTime: utcEndTime } = {
        utcDate: mode === "single" ? startDate : endDate,
        utcTime: endTime,
      };

      await onReserve({
        startDate: utcStartDate,
        endDate: utcEndDate,
        startTime: utcStartTime,
        endTime: utcEndTime,
        isAllDay: false,
        reservationType: mode,
        recurrencePattern: mode === "recurring" ? recurrencePattern : undefined,
        customDays:
          mode === "recurring" && recurrencePattern === "custom"
            ? customDays
            : undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to create reservation");
    } finally {
      setSaving(false);
    }
  };

  const getModeDescription = () => {
    if (mode === "single") {
      return t("reservations.singleModeDescription", {
        date: startDate
          ? new Date(startDate).toDateString()
          : t("reservations.selectedDate"),
      });
    }

    if (mode === "continuous") {
      return t("reservations.continuousModeDescription");
    }

    return t("reservations.multiDayModeDescription");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          bgcolor: themeColors.cardBg,
          borderBottom: `1px solid ${themeColors.border}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("reservations.reserve")} {asset?.name}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#64748b" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
        {/* Reservation Type Tabs */}
        <Box
          sx={{
            display: "flex",
            p: 0.5,
            borderRadius: 2,
            mb: 3,
            border: `1px solid ${themeColors.borderLight}`,
            bgcolor: themeColors.inputBg,
          }}
        >
          {(["single", "continuous", "recurring"] as ReservationMode[]).map(
            (type) => (
              <Button
                key={type}
                type="button"
                onClick={() => setMode(type)}
                sx={{
                  flex: 1,
                  py: 1,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  borderRadius: 1.5,
                  transition: "all 0.2s",
                  ...(mode === type
                    ? {
                        color: themeColors.primary,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        border: `1px solid ${themeColors.primary}`,
                      }
                    : {
                        color: themeColors.textMuted,
                        "&:hover": { color: themeColors.textPrimary },
                      }),
                }}
              >
                {t(
                  type === "continuous"
                    ? "reservations.multiDay"
                    : `reservations.${type}`,
                )}
              </Button>
            ),
          )}
        </Box>

        {/* Info Box */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
            mb: 3,
            border: "1px solid #e0e7ff",
          }}
        >
          <InfoIcon sx={{ fontSize: 18, mt: 0.25, flexShrink: 0 }} />
          <Typography
            variant="body2"
            sx={{ fontSize: "0.75rem", lineHeight: 1.5 }}
          >
            {getModeDescription()}
          </Typography>
        </Box>

        {/* Date Selection */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: mode === "single" ? "1fr" : "1fr 1fr",
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              component="label"
              sx={{
                display: "block",
                fontSize: "0.625rem",
                fontWeight: 700,
                color: themeColors.textSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 0.5,
              }}
            >
              {mode === "single"
                ? t("reservations.date")
                : t("reservations.startDate")}
            </Typography>
            <input
              type="date"
              required
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                fontSize: "0.875rem",
                outline: "none",
                color: themeColors.textPrimary,
                borderColor: themeColors.border,
                backgroundColor: themeColors.inputBg,
              }}
            />
          </Box>
          {mode !== "single" && (
            <Box>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  color: themeColors.textSecondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  mb: 0.5,
                }}
              >
                {t("reservations.endDate")}
              </Typography>
              <input
                type="date"
                required
                min={startDate}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  fontSize: "0.875rem",
                  outline: "none",
                  color: themeColors.textPrimary,
                  borderColor: themeColors.border,
                  backgroundColor: themeColors.inputBg,
                }}
              />
            </Box>
          )}
        </Box>

        {/* Time Selection */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              component="label"
              sx={{
                display: "block",
                fontSize: "0.625rem",
                fontWeight: 700,
                color: themeColors.textSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 0.5,
              }}
            >
              {t("reservations.startTime")}
            </Typography>
            <Box sx={{ position: "relative" }}>
              <input
                type="time"
                required
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px 10px 10px",
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  outline: "none",
                  color: themeColors.textPrimary,
                  borderColor: themeColors.border,
                  backgroundColor: themeColors.inputBg,
                }}
              />
            </Box>
          </Box>
          <Box>
            <Typography
              component="label"
              sx={{
                display: "block",
                fontSize: "0.625rem",
                fontWeight: 700,
                color: themeColors.textSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 0.5,
              }}
            >
              {t("reservations.endTime")}
            </Typography>
            <Box sx={{ position: "relative" }}>
              <input
                type="time"
                required
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px 10px 10px",
                  border: "1px solid #cbd5e1",
                  borderRadius: 8,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  outline: "none",
                  color: themeColors.textPrimary,
                  borderColor: themeColors.border,
                  backgroundColor: themeColors.inputBg,
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Recurrence Options (only for recurring mode) */}
        {mode === "recurring" && (
          <Box sx={{ pt: 2, borderTop: "1px solid #f1f5f9", mb: 3 }}>
            <Typography
              component="label"
              sx={{
                display: "block",
                fontSize: "0.625rem",
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 1.5,
              }}
            >
              {t("reservations.recurrencePattern")}
            </Typography>
            <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
              {(["daily", "weekly", "custom"] as RecurrencePattern[]).map(
                (pat) => (
                  <Box
                    key={pat}
                    component="label"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontSize: "0.875rem",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      name="recurrence"
                      checked={recurrencePattern === pat}
                      onChange={() => setRecurrencePattern(pat)}
                      style={{ accentColor: "#6db1e7" }}
                    />
                    <span style={{ textTransform: "capitalize" }}>
                      {t(`reservations.${pat}`)}
                    </span>
                  </Box>
                ),
              )}
            </Box>

            {recurrencePattern === "custom" && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 0.5,
                }}
              >
                {weekDays.map((day, idx) => (
                  <Button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(idx)}
                    sx={{
                      minWidth: 36,
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      transition: "all 0.2s",
                      ...(customDays[idx]
                        ? {
                            bgcolor: "#6db1e7",
                            color: "white",
                            "&:hover": { bgcolor: "#4338ca" },
                          }
                        : {
                            bgcolor: "#f1f5f9",
                            color: "#94a3b8",
                            "&:hover": { bgcolor: "#e2e8f0" },
                          }),
                    }}
                  >
                    {day.charAt(0)}
                  </Button>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Error Display */}
        {error && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: "#fef2f2",
              color: "#dc2626",
              fontSize: "0.875rem",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 3,
              border: "1px solid #fecaca",
            }}
          >
            <InfoIcon sx={{ fontSize: 16, flexShrink: 0 }} />
            <span>{error}</span>
          </Box>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, pt: 1 }}>
          <Button
            type="button"
            onClick={onClose}
            disabled={saving}
            sx={{
              flex: 1,
              py: 1.25,
              border: "1px solid #cbd5e1",

              borderRadius: 2,
              fontWeight: 500,
            }}
          >
            {t("common.cancel")}
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={saving || !isFormValid}
            startIcon={<CheckCircleIcon />}
            sx={{
              flex: 1,
              borderRadius: 2,
              py: 1.2,
              bgcolor: themeColors.primary,
              "&:hover": { bgcolor: themeColors.primaryHover },
            }}
          >
            {saving ? t("reservations.processing") : t("common.confirm")}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ReservationDialog;
